import React, { useEffect, useState } from "react";
import { Loader2, UserPlus, RefreshCw, MailCheck, X, ChevronDown, ChevronUp, ShieldAlert, ShieldCheck, CheckCircle, TrendingUp, Plus, Upload, Download, CheckCircle2, AlertCircle, Pencil, Trash2, UserX, UserCheck, Search } from "lucide-react";
import { adminApi, formatApiError } from "../../lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const NIVEL_LABELS = { 1: "Iniciación", 2: "Intermedio", 3: "Avanzado" };
const ESTADO_BADGE = {
  invitado:           { label: "Invitado",       cls: "bg-yellow-500/15 text-yellow-300 border border-yellow-500/30" },
  activo:             { label: "Activo",          cls: "bg-green-500/15 text-green-300 border border-green-500/30" },
  pendiente_revision: { label: "Pend. revisión", cls: "bg-red-500/15 text-red-300 border border-red-500/30" },
  inactivo:           { label: "Inactivo",        cls: "bg-white/8 text-white/35 border border-white/15" },
};
const PAR_Q_LABELS = {
  problema_cardiaco:           "Problema cardíaco o actividad limitada",
  dolor_pecho_ejercicio:       "Dolor en el pecho durante ejercicio",
  mareos_desmayos:             "Mareos, desmayos o pérdida de conocimiento",
  falta_aire_inusual:          "Falta de aire inusual en esfuerzos normales",
  otra_condicion_medica:       "Otra condición médica",
  medicacion_relevante:        "Medicación relevante para el ejercicio",
  lesion_reciente_sin_recuperar: "Lesión reciente sin recuperar",
};
const OBJETIVO_LABELS = {
  salud_general: "Salud general",
  perder_grasa: "Perder grasa",
  ganar_musculo: "Ganar músculo",
  ganar_fuerza: "Ganar fuerza",
  resistencia: "Resistencia",
  recuperacion_lesion: "Recuperación de lesión",
};
const EXP_LABELS = {
  nunca: "Sin experiencia previa",
  ocasional: "Alguna vez",
  habitual: "Entrenamiento habitual",
};
const SUPERVISION_LABELS = {
  por_mi_cuenta: "Por mi cuenta",
  programa_preparado: "Con programa preparado",
  con_entrenador: "Con entrenador personal",
  indiferente: "Indiferente",
};

export default function SociosTab() {
  const [users, setUsers] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ nombre_completo: "", email: "", telefono: "", fecha_nacimiento: "", profesion: "" });
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const [createOk, setCreateOk] = useState("");
  const [reinviting, setReinviting] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [activating, setActivating] = useState(false);
  const [assignRoutineId, setAssignRoutineId] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [evolutionData, setEvolutionData] = useState([]);
  const [evolutionLoading, setEvolutionLoading] = useState(false);
  const [addingEvo, setAddingEvo] = useState(false);
  const [newEvoPeso, setNewEvoPeso] = useState("");
  const [newEvoNotas, setNewEvoNotas] = useState("");
  const [evoOk, setEvoOk] = useState(false);
  const [evoErr, setEvoErr] = useState("");

  // Import modal state
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importErr, setImportErr] = useState("");

  // Edit / delete / deactivate state
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const [u, r] = await Promise.all([adminApi.listUsers(), adminApi.listRoutines()]);
      setUsers(u); setRoutines(r);
    } catch { setErr("Error cargando datos"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true); setCreateErr(""); setCreateOk("");
    try {
      await adminApi.createUser({ ...form });
      setCreateOk(`Socio dado de alta. Email enviado a ${form.email}.`);
      setForm({ nombre_completo: "", email: "", telefono: "", fecha_nacimiento: "", profesion: "" });
      setFormOpen(false);
      await load();
    } catch (e) { setCreateErr(formatApiError(e?.response?.data?.detail) || "Error al dar de alta"); }
    finally { setCreating(false); }
  };

  const handleReinvite = async (userId) => {
    setReinviting(userId);
    try {
      await adminApi.reinviteUser(userId);
      await load();
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Error"); }
    finally { setReinviting(null); }
  };

  const openUserDetail = async (u) => {
    setSelectedUser(u);
    setUserDetail(null);
    setDetailLoading(true);
    setAssignRoutineId("");
    setEvolutionData([]);
    setNewEvoPeso(""); setNewEvoNotas(""); setEvoOk(false); setEvoErr("");
    setEditMode(false); setSaveErr("");
    try {
      const [detail, evo] = await Promise.all([
        adminApi.getUserDetail(u.id),
        adminApi.getUserEvolution(u.id).catch(() => []),
      ]);
      setUserDetail(detail);
      setEvolutionData(Array.isArray(evo) ? evo : []);
    } catch { /* show basic info */ }
    finally { setDetailLoading(false); }
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;
    setSaving(true); setSaveErr("");
    try {
      await adminApi.updateUser(selectedUser.id, editForm);
      await load();
      const detail = await adminApi.getUserDetail(selectedUser.id);
      setUserDetail(detail);
      setSelectedUser((prev) => ({ ...prev, ...editForm }));
      setEditMode(false);
    } catch (e) { setSaveErr(e?.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    if (!window.confirm(`¿Eliminar a ${selectedUser.nombre_completo}? Esta acción es irreversible y borrará todos sus datos.`)) return;
    setDeleting(true);
    try {
      await adminApi.deleteUser(selectedUser.id);
      setSelectedUser(null); setUserDetail(null);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || "Error al eliminar"); }
    finally { setDeleting(false); }
  };

  const handleToggleActive = async () => {
    if (!selectedUser) return;
    setToggling(true);
    try {
      const isActive = selectedUser.estado_cuenta === "activo";
      if (isActive) {
        await adminApi.deactivateUser(selectedUser.id);
        setSelectedUser((prev) => ({ ...prev, estado_cuenta: "inactivo" }));
        setUserDetail((prev) => prev ? { ...prev, user: { ...prev.user, estado_cuenta: "inactivo" } } : null);
      } else {
        await adminApi.reactivateUser(selectedUser.id);
        setSelectedUser((prev) => ({ ...prev, estado_cuenta: "activo" }));
        setUserDetail((prev) => prev ? { ...prev, user: { ...prev.user, estado_cuenta: "activo" } } : null);
      }
      await load();
    } catch (e) { alert(e?.response?.data?.detail || "Error"); }
    finally { setToggling(false); }
  };

  const handleAddEvolution = async () => {
    if (!newEvoPeso || !selectedUser) return;
    setAddingEvo(true); setEvoErr("");
    try {
      await adminApi.addUserEvolution(selectedUser.id, { peso_kg: parseFloat(newEvoPeso), notas: newEvoNotas || null });
      const evo = await adminApi.getUserEvolution(selectedUser.id);
      setEvolutionData(Array.isArray(evo) ? evo : []);
      setNewEvoPeso(""); setNewEvoNotas(""); setEvoOk(true);
      setTimeout(() => setEvoOk(false), 3000);
    } catch (e) { setEvoErr(formatApiError(e?.response?.data?.detail) || "Error"); }
    finally { setAddingEvo(false); }
  };

  const handleActivate = async () => {
    if (!selectedUser) return;
    setActivating(true);
    try {
      await adminApi.activateUser(selectedUser.id);
      await load();
      setSelectedUser((prev) => ({ ...prev, estado_cuenta: "activo" }));
      setUserDetail((prev) => prev ? { ...prev, user: { ...prev.user, estado_cuenta: "activo" } } : null);
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Error"); }
    finally { setActivating(false); }
  };

  const handleAssign = async () => {
    if (!assignRoutineId || !selectedUser) return;
    // Safety: check for level jump > 1
    const newRoutine = routines.find((r) => r.id === assignRoutineId);
    const currentNivel = userDetail?.rutina?.nivel || null;
    if (newRoutine && currentNivel && newRoutine.nivel > currentNivel + 1) {
      const jump = newRoutine.nivel - currentNivel;
      const confirmed = window.confirm(
        `Atención: Estás saltando ${jump} nivel(es) de golpe (${NIVEL_LABELS[currentNivel]} → ${NIVEL_LABELS[newRoutine.nivel]}).\n\n` +
        `Esto puede suponer un riesgo para el socio si no tiene la preparación física adecuada.\n\n` +
        `¿Confirmas el cambio de nivel?`
      );
      if (!confirmed) return;
    }
    setAssigning(true);
    try {
      await adminApi.assignRoutine(selectedUser.id, assignRoutineId);
      await load();
      const detail = await adminApi.getUserDetail(selectedUser.id);
      setUserDetail(detail);
      const updated = users.find((u) => u.id === selectedUser.id);
      if (updated) setSelectedUser(updated);
      setAssignRoutineId("");
    } catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Error asignando rutina"); }
    finally { setAssigning(false); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("es-ES") : "—";

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true); setImportErr(""); setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const result = await adminApi.importarSocios(formData);
      setImportResult(result);
      await load();
    } catch (e) { setImportErr(formatApiError(e?.response?.data?.detail) || "Error al importar"); }
    finally { setImporting(false); }
  };

  const downloadTemplate = () => {
    const csv = "nombre_completo,email,telefono,fecha_nacimiento,profesion\nJuan García López,juan@email.com,612345678,1990-05-20,Ingeniero\nMaría Pérez Ros,maria@email.com,698765432,,";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "plantilla_socios.csv";
    a.click();
  };

  const FIELDS = [
    { key: "nombre_completo", label: "Nombre completo", type: "text", placeholder: "Juan García López", required: true },
    { key: "email", label: "Email", type: "email", placeholder: "socio@email.com", required: true },
    { key: "telefono", label: "Teléfono", type: "tel", placeholder: "612 345 678", required: true },
    { key: "fecha_nacimiento", label: "Fecha nacimiento (opcional)", type: "date", required: false },
    { key: "profesion", label: "Profesión (opcional)", type: "text", placeholder: "Ingeniero", required: false },
  ];

  return (
    <div className="p-6 lg:p-8" data-testid="socios-tab">
      <div className="flex items-center justify-between mb-7">
        <div>
          <h1 className="text-xl font-black tracking-wider">Socios</h1>
          <p className="text-xs text-white/40 mt-0.5">{users.length} socio(s) registrado(s)</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setImportOpen(true); setImportFile(null); setImportResult(null); setImportErr(""); }} data-testid="toggle-import-modal"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 text-white/60 font-black text-[11px] tracking-[0.2em] uppercase hover:border-white/30 hover:text-white transition-colors">
            <Upload size={14} /> Importar
          </button>
          <button onClick={() => setFormOpen((v) => !v)} data-testid="toggle-alta-form"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#FFCC00] text-black font-black text-[11px] tracking-[0.2em] uppercase hover:bg-white transition-colors">
            <UserPlus size={14} /> Dar de alta
            {formOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
        </div>
      </div>

      {/* Alta form (collapsible) */}
      {formOpen && (
        <div className="mb-8 border border-white/10 bg-[#0a0a0a]">
          <div className="border-b border-white/10 px-6 py-4 flex items-center gap-2">
            <UserPlus size={14} className="text-[#FFCC00]" />
            <h2 className="text-xs font-black tracking-[0.2em] uppercase">Nuevo socio</h2>
          </div>
          <form onSubmit={handleCreate} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="alta-form">
            {FIELDS.map(({ key, label, type, placeholder, required }) => (
              <div key={key}>
                <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-white/50 mb-1.5">{label}</label>
                <input type={type} value={form[key]} onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                  placeholder={placeholder} required={required} data-testid={`alta-${key}`}
                  className="w-full bg-black border border-white/12 text-white placeholder:text-white/20 px-4 py-2.5 text-sm outline-none focus:border-[#FFCC00] transition-colors" />
              </div>
            ))}
            <div className="sm:col-span-2 flex flex-col gap-3">
              {createErr && <p className="text-red-400 text-sm" data-testid="alta-error">{createErr}</p>}
              {createOk && <div className="flex items-center gap-2 text-green-300 text-sm" data-testid="alta-success"><MailCheck size={15} /> {createOk}</div>}
              <button type="submit" disabled={creating} data-testid="alta-submit"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#FFCC00] text-black font-black text-[11px] tracking-[0.2em] uppercase hover:bg-white transition-colors disabled:opacity-40 self-start">
                {creating ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />}
                {creating ? "Dando de alta..." : "Dar de alta y enviar invitación"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users table */}
      <div className="flex items-center justify-between mb-4 gap-3">
        <h2 className="text-xs font-black tracking-[0.2em] uppercase text-white/60 shrink-0">Lista de socios</h2>
        <div className="flex items-center gap-2 flex-1 max-w-xs">
          <div className="relative flex-1">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por nombre o email..."
              data-testid="socios-search"
              className="w-full bg-white/5 border border-white/10 text-white text-xs pl-7 pr-3 py-1.5 outline-none focus:border-[#FFCC00]/50 placeholder-white/25"
            />
          </div>
          {search && (
            <button onClick={() => setSearch("")} className="text-white/30 hover:text-white transition-colors">
              <X size={13} />
            </button>
          )}
        </div>
        <button onClick={load} disabled={loading} className="flex items-center gap-1.5 text-white/40 hover:text-white text-xs transition-colors shrink-0">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refrescar
        </button>
      </div>

      {err && <p className="text-red-400 text-sm mb-4">{err}</p>}

      <div className="border border-white/10 overflow-x-auto" data-testid="socios-list">
        <table className="w-full min-w-[700px] text-sm">
          <thead className="bg-white/4 border-b border-white/8">
            <tr>{["Socio", "Estado", "Rutina asignada", "Alta", "Activación", ""].map((h) => (
              <th key={h} className="px-4 py-3 text-left text-[10px] tracking-[0.18em] uppercase text-white/45 font-bold">{h}</th>
            ))}</tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center"><Loader2 size={18} className="animate-spin mx-auto text-white/30" /></td></tr>
            ) : (() => {
              const filtered = users.filter(u => {
                if (!search.trim()) return true;
                const q = search.toLowerCase();
                return u.nombre_completo?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
              });
              if (filtered.length === 0) return (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-white/30 text-sm">{search ? `Sin resultados para "${search}"` : "Sin socios todavía. Da de alta al primero."}</td></tr>
              );
              return filtered.map((u) => {
              const badge = ESTADO_BADGE[u.estado_cuenta] || { label: u.estado_cuenta, cls: "bg-white/10 text-white/50" };
              return (
                <tr key={u.id} className="hover:bg-white/[0.02] cursor-pointer" data-testid={`socio-row-${u.id}`} onClick={() => openUserDetail(u)}>
                  <td className="px-4 py-3">
                    <p className="font-bold text-white">{u.nombre_completo}</p>
                    <p className="text-[11px] text-white/40">{u.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-[10px] font-bold tracking-wider uppercase px-2 py-1 ${badge.cls}`}>{badge.label}</span>
                  </td>
                  <td className="px-4 py-3">
                    {u.rutina_nombre ? (
                      <div>
                        <p className="text-xs font-bold text-white">{u.rutina_nombre}</p>
                        <p className="text-[10px] text-white/35">{NIVEL_LABELS[u.rutina_nivel] || `Nivel ${u.rutina_nivel}`}</p>
                      </div>
                    ) : (
                      <span className="text-[11px] text-white/25">Sin rutina</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-white/45">{fmtDate(u.fecha_invitacion)}</td>
                  <td className="px-4 py-3 text-xs text-white/45">{fmtDate(u.fecha_activacion)}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    {u.estado_cuenta !== "activo" && (
                      <button onClick={() => handleReinvite(u.id)} disabled={reinviting === u.id} data-testid={`reinvitar-${u.id}`}
                        className="text-[10px] font-bold tracking-wider uppercase text-[#FFCC00] hover:text-white transition-colors disabled:opacity-40">
                        {reinviting === u.id ? "..." : "Reinvitar"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            });
            })()}
          </tbody>
        </table>
      </div>

      {/* User detail modal */}
      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          detail={userDetail}
          detailLoading={detailLoading}
          routines={routines}
          assignRoutineId={assignRoutineId}
          setAssignRoutineId={setAssignRoutineId}
          assigning={assigning}
          activating={activating}
          handleActivate={handleActivate}
          handleAssign={handleAssign}
          handleReinvite={handleReinvite}
          reinviting={reinviting}
          evolutionData={evolutionData}
          addingEvo={addingEvo}
          newEvoPeso={newEvoPeso}
          setNewEvoPeso={setNewEvoPeso}
          newEvoNotas={newEvoNotas}
          setNewEvoNotas={setNewEvoNotas}
          evoOk={evoOk}
          evoErr={evoErr}
          handleAddEvolution={handleAddEvolution}
          editMode={editMode}
          setEditMode={(v) => { setEditMode(v); if (v) setEditForm({ nombre_completo: selectedUser.nombre_completo, telefono: selectedUser.telefono, fecha_nacimiento: selectedUser.fecha_nacimiento || "", profesion: selectedUser.profesion || "" }); setSaveErr(""); }}
          editForm={editForm}
          setEditForm={setEditForm}
          saving={saving}
          saveErr={saveErr}
          handleSaveEdit={handleSaveEdit}
          deleting={deleting}
          handleDelete={handleDelete}
          toggling={toggling}
          handleToggleActive={handleToggleActive}
          onClose={() => { setSelectedUser(null); setUserDetail(null); setAssignRoutineId(""); setEvolutionData([]); setEditMode(false); setSaveErr(""); }}
        />
      )}

      {/* Import modal */}
      {importOpen && (
        <ImportModal
          importFile={importFile}
          setImportFile={setImportFile}
          importing={importing}
          importResult={importResult}
          importErr={importErr}
          onImport={handleImport}
          onDownloadTemplate={downloadTemplate}
          onClose={() => { setImportOpen(false); setImportFile(null); setImportResult(null); setImportErr(""); }}
        />
      )}
    </div>
  );
}

function UserDetailModal({ user, detail, detailLoading, routines, assignRoutineId, setAssignRoutineId, assigning, activating, handleActivate, handleAssign, handleReinvite, reinviting, evolutionData, addingEvo, newEvoPeso, setNewEvoPeso, newEvoNotas, setNewEvoNotas, evoOk, evoErr, handleAddEvolution, editMode, setEditMode, editForm, setEditForm, saving, saveErr, handleSaveEdit, deleting, handleDelete, toggling, handleToggleActive, onClose }) {
  const badge = ESTADO_BADGE[user.estado_cuenta] || { label: user.estado_cuenta, cls: "bg-white/10 text-white/50" };
  const initials = user.nombre_completo?.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const safety = detail?.safety;
  const personalization = detail?.personalization;
  const rutina = detail?.rutina;
  const u = detail?.user || user; // prefer live data
  const isActive = u.estado_cuenta === "activo";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="user-detail-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[#0d0d0d] border border-white/10 shadow-2xl max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0d0d0d] border-b border-white/8 px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#FFCC00] text-black flex items-center justify-center font-black text-sm">{initials}</div>
            <div>
              <p className="font-bold text-sm">{u.nombre_completo}</p>
              <p className="text-[11px] text-white/40">{u.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Edit */}
            <button onClick={() => setEditMode(!editMode)} data-testid="edit-user-btn"
              title="Editar datos"
              className={`p-1.5 transition-colors ${editMode ? "text-[#FFCC00]" : "text-white/30 hover:text-white"}`}>
              <Pencil size={15} />
            </button>
            {/* Desactivar / Reactivar */}
            <button onClick={handleToggleActive} disabled={toggling} data-testid="toggle-active-btn"
              title={isActive ? "Desactivar socio" : "Reactivar socio"}
              className={`p-1.5 transition-colors ${isActive ? "text-white/30 hover:text-red-400" : "text-green-400/60 hover:text-green-300"} disabled:opacity-40`}>
              {toggling ? <Loader2 size={15} className="animate-spin" /> : isActive ? <UserX size={15} /> : <UserCheck size={15} />}
            </button>
            {/* Eliminar */}
            <button onClick={handleDelete} disabled={deleting} data-testid="delete-user-btn"
              title="Eliminar socio"
              className="p-1.5 text-white/20 hover:text-red-400 transition-colors disabled:opacity-40">
              {deleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
            </button>
            <div className="w-px h-5 bg-white/10 mx-1" />
            <button onClick={onClose} className="text-white/35 hover:text-white transition-colors"><X size={17} /></button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-6">

          {/* Edit form */}
          {editMode && (
            <div className="border border-[#FFCC00]/25 bg-[#FFCC00]/4 p-4 space-y-3" data-testid="edit-user-form">
              <p className="text-[10px] tracking-[0.2em] uppercase font-black text-[#FFCC00] mb-1">Editar datos</p>
              {[
                { key: "nombre_completo", label: "Nombre completo", type: "text" },
                { key: "telefono", label: "Teléfono", type: "tel" },
                { key: "fecha_nacimiento", label: "Fecha nacimiento", type: "date" },
                { key: "profesion", label: "Profesión", type: "text" },
              ].map(({ key, label, type }) => (
                <div key={key}>
                  <label className="block text-[10px] tracking-[0.15em] uppercase font-bold text-white/40 mb-1">{label}</label>
                  <input type={type} value={editForm[key] || ""} onChange={(e) => setEditForm((p) => ({ ...p, [key]: e.target.value }))}
                    data-testid={`edit-${key}`}
                    className="w-full bg-black border border-white/20 text-white px-3 py-2 text-sm outline-none focus:border-[#FFCC00] transition-colors" />
                </div>
              ))}
              {saveErr && <p className="text-red-400 text-xs">{saveErr}</p>}
              <div className="flex gap-2 pt-1">
                <button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#FFCC00] text-black font-black text-[11px] tracking-[0.15em] uppercase hover:bg-white transition-colors disabled:opacity-40">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                  {saving ? "Guardando..." : "Guardar cambios"}
                </button>
                <button onClick={() => setEditMode(false)}
                  className="px-4 py-2 border border-white/15 text-white/50 font-black text-[11px] tracking-[0.15em] uppercase hover:text-white transition-colors">
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Estado + acciones */}
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-[10px] font-black tracking-widest uppercase px-3 py-1.5 ${badge.cls}`} data-testid="user-estado-badge">
              {badge.label}
            </span>
            {u.estado_cuenta === "pendiente_revision" && (
              <button onClick={handleActivate} disabled={activating} data-testid="activate-user-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500/15 border border-green-500/40 text-green-300 font-black text-[10px] tracking-[0.2em] uppercase hover:bg-green-500/25 transition-colors disabled:opacity-40">
                {activating ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                {activating ? "Activando..." : "Activar cuenta"}
              </button>
            )}
            {u.estado_cuenta !== "activo" && (
              <button onClick={() => handleReinvite(u.id)} disabled={reinviting === u.id} data-testid={`modal-reinvitar-${u.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#FFCC00]/35 text-[#FFCC00] font-black text-[10px] tracking-[0.2em] uppercase hover:bg-[#FFCC00]/10 transition-colors disabled:opacity-40">
                {reinviting === u.id ? "..." : "Reenviar invitación"}
              </button>
            )}
          </div>

          {/* Info personal */}
          <div className="grid grid-cols-2 gap-3">
            {[
              ["Teléfono", u.telefono],
              ["Profesión", u.profesion],
              ["Nacimiento", u.fecha_nacimiento],
              ["Alta (invitado)", u.fecha_invitacion ? new Date(u.fecha_invitacion).toLocaleDateString("es-ES") : "—"],
              ["Activación", u.fecha_activacion ? new Date(u.fecha_activacion).toLocaleDateString("es-ES") : "—"],
            ].map(([l, v]) => (
              <div key={l}>
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/30 mb-0.5">{l}</p>
                <p className="text-sm text-white/80">{v || "—"}</p>
              </div>
            ))}
          </div>

          {detailLoading && (
            <div className="flex items-center gap-2 text-white/30 text-sm">
              <Loader2 size={14} className="animate-spin" /> Cargando datos del cuestionario...
            </div>
          )}

          {/* PAR-Q */}
          {safety && (
            <div data-testid="parq-section">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40 mb-3">Cuestionario de seguridad (PAR-Q)</p>
              {safety.hay_bandera_roja ? (
                <div className="border border-red-500/40 bg-red-500/8 px-4 py-3 mb-3 flex items-start gap-2" data-testid="parq-red-flag">
                  <ShieldAlert size={15} className="text-red-400 shrink-0 mt-0.5" />
                  <p className="text-red-300 font-black text-xs tracking-wider uppercase">Alerta médica detectada</p>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-green-300 text-xs mb-3" data-testid="parq-ok">
                  <ShieldCheck size={14} /> Sin alertas médicas
                </div>
              )}
              <div className="space-y-1.5 mb-3">
                {Object.entries(PAR_Q_LABELS).map(([key, label]) => {
                  const val = safety[key];
                  return (
                    <div key={key} className="flex items-start gap-2 text-[11px]">
                      <span className={`mt-0.5 font-black shrink-0 ${val ? "text-red-400" : "text-white/25"}`}>{val ? "SI" : "NO"}</span>
                      <span className={val ? "text-white/80" : "text-white/35"}>{label}
                        {key === "otra_condicion_medica" && safety.otra_condicion_medica_detalle && val && (
                          <span className="text-white/50"> — {safety.otra_condicion_medica_detalle}</span>
                        )}
                        {key === "medicacion_relevante" && safety.medicacion_relevante_detalle && val && (
                          <span className="text-white/50"> — {safety.medicacion_relevante_detalle}</span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px] text-white/40 pt-2 border-t border-white/8">
                <div><span className="text-white/25">Firmante: </span>{safety.consentimiento_nombre_firmante || "—"}</div>
                <div><span className="text-white/25">Fecha: </span>{safety.consentimiento_fecha ? new Date(safety.consentimiento_fecha).toLocaleDateString("es-ES") : "—"}</div>
              </div>
            </div>
          )}

          {/* Personalización */}
          {personalization && (
            <div data-testid="personalization-section">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40 mb-3">Cuestionario de personalización</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-white/30 mb-0.5">Objetivo</p>
                  <p className="text-xs font-bold text-white">{OBJETIVO_LABELS[personalization.objetivo_principal] || personalization.objetivo_principal || "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] text-white/30 mb-0.5">Experiencia</p>
                  <p className="text-xs font-bold text-white">{EXP_LABELS[personalization.experiencia_previa] || personalization.experiencia_previa || "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] text-white/30 mb-0.5">Días/semana</p>
                  <p className="text-xs font-bold text-white">{personalization.dias_semana_deseados ? `${personalization.dias_semana_deseados} días` : "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] text-white/30 mb-0.5">Supervisión</p>
                  <p className="text-xs font-bold text-white">{SUPERVISION_LABELS[personalization.preferencia_supervision] || personalization.preferencia_supervision || "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] text-white/30 mb-0.5">Peso</p>
                  <p className="text-xs font-bold text-white" data-testid="user-peso">{personalization.peso_kg != null ? `${personalization.peso_kg} kg` : "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] text-white/30 mb-0.5">Altura</p>
                  <p className="text-xs font-bold text-white" data-testid="user-altura">{personalization.altura_cm != null ? `${personalization.altura_cm} cm` : "—"}</p>
                </div>
              </div>
            </div>
          )}

          {/* Evolución de peso */}
          <div data-testid="evolution-section">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={13} className="text-[#FFCC00]" />
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40">Evolución de peso</p>
            </div>

            {evolutionData.length >= 2 && (
              <div className="mb-3">
                <ResponsiveContainer width="100%" height={130}>
                  <LineChart data={evolutionData.map((r) => ({ fecha: r.fecha.slice(5), peso: r.peso_kg }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="fecha" tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 9 }} axisLine={false} tickLine={false} />
                    <YAxis domain={["auto","auto"]} tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 9 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}kg`} />
                    <Tooltip contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0 }}
                      labelStyle={{ color: "rgba(255,255,255,0.4)", fontSize: 10 }}
                      itemStyle={{ color: "#FFCC00", fontWeight: "bold" }}
                      formatter={(v) => [`${v} kg`, "Peso"]} />
                    <Line type="monotone" dataKey="peso" stroke="#FFCC00" strokeWidth={2} dot={{ fill: "#FFCC00", r: 3, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {evolutionData.length > 0 && (
              <div className="border border-white/8 divide-y divide-white/5 mb-3 max-h-28 overflow-y-auto">
                {[...evolutionData].reverse().slice(0, 5).map((r, i) => (
                  <div key={r.id || i} className="flex items-center justify-between px-3 py-2 text-xs">
                    <span className="font-bold text-white">{r.peso_kg} kg</span>
                    <div className="text-right">
                      <span className="text-white/40">{new Date(r.fecha + "T00:00:00").toLocaleDateString("es-ES", { day:"numeric", month:"short" })}</span>
                      {r.fuente === "staff" && <span className="ml-1.5 text-[9px] text-[#FFCC00]/50">Staff</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {evolutionData.length === 0 && !detailLoading && (
              <p className="text-xs text-white/25 mb-3">Sin registros de peso todavía.</p>
            )}

            {/* Add measurement */}
            <div className="flex gap-2">
              <input type="number" min="20" max="300" step="0.1" value={newEvoPeso}
                onChange={(e) => setNewEvoPeso(e.target.value)} placeholder="Peso kg"
                data-testid="admin-evo-peso"
                className="flex-1 bg-black border border-white/10 text-white placeholder:text-white/20 px-3 py-2 text-sm outline-none focus:border-[#FFCC00] transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <input value={newEvoNotas} onChange={(e) => setNewEvoNotas(e.target.value)} placeholder="Notas (opcional)"
                data-testid="admin-evo-notas"
                className="flex-[2] bg-black border border-white/10 text-white placeholder:text-white/20 px-3 py-2 text-sm outline-none focus:border-[#FFCC00] transition-colors"
              />
              <button onClick={handleAddEvolution} disabled={!newEvoPeso || addingEvo} data-testid="admin-evo-submit"
                className="px-3 py-2 bg-[#FFCC00] text-black font-black text-xs hover:bg-white transition-colors disabled:opacity-40">
                {addingEvo ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
              </button>
            </div>
            {evoErr && <p className="text-red-400 text-xs mt-1">{evoErr}</p>}
            {evoOk && <p className="text-green-400 text-xs mt-1">Registro añadido</p>}
          </div>

          {/* Rutina asignada */}
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40 mb-2">Rutina asignada</p>
            {rutina ? (
              <div className="border border-[#FFCC00]/20 bg-[#FFCC00]/5 px-4 py-3" data-testid="assigned-routine-info">
                <p className="font-bold text-sm text-[#FFCC00]">{rutina.nombre}</p>
                <p className="text-[11px] text-white/40 mt-0.5">{NIVEL_LABELS[rutina.nivel] || `Nivel ${rutina.nivel}`}</p>
              </div>
            ) : (
              <p className="text-sm text-white/25">Sin rutina asignada</p>
            )}
          </div>

          {/* Asignar rutina */}
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40 mb-2">
              {rutina ? "Cambiar rutina" : "Asignar rutina"}
            </p>
            <div className="flex gap-2">
              <select value={assignRoutineId} onChange={(e) => setAssignRoutineId(e.target.value)} data-testid="assign-routine-select"
                className="flex-1 bg-black border border-white/12 text-white px-3 py-2.5 text-sm outline-none focus:border-[#FFCC00] appearance-none">
                <option value="">— Seleccionar rutina —</option>
                {routines.map((r) => (
                  <option key={r.id} value={r.id}>{r.nombre} · {NIVEL_LABELS[r.nivel] || `N${r.nivel}`}</option>
                ))}
              </select>
              <button onClick={handleAssign} disabled={!assignRoutineId || assigning} data-testid="assign-routine-btn"
                className="px-4 py-2.5 bg-[#FFCC00] text-black font-black text-[10px] tracking-[0.2em] uppercase hover:bg-white transition-colors disabled:opacity-40">
                {assigning ? <Loader2 size={13} className="animate-spin" /> : "Asignar"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Import Modal ----

function ImportModal({ importFile, setImportFile, importing, importResult, importErr, onImport, onDownloadTemplate, onClose }) {
  const inputRef = React.useRef(null);
  const hasResult = importResult !== null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="import-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={!importing ? onClose : undefined} />
      <div className="relative w-full max-w-lg bg-[#0d0d0d] border border-white/10 shadow-2xl">

        {/* Header */}
        <div className="border-b border-white/8 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Upload size={14} className="text-[#FFCC00]" />
            <h2 className="text-xs font-black tracking-[0.2em] uppercase">Importar socios</h2>
          </div>
          <button onClick={onClose} disabled={importing} className="text-white/35 hover:text-white transition-colors disabled:opacity-40">
            <X size={17} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Template download */}
          <div className="border border-white/8 bg-white/[0.02] px-4 py-3 flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold text-white mb-0.5">Formato del archivo</p>
              <p className="text-[11px] text-white/40">Columnas: <span className="text-white/65 font-mono">nombre_completo, email, telefono</span></p>
              <p className="text-[11px] text-white/30 mt-0.5">Opcionales: <span className="font-mono">fecha_nacimiento, profesion</span></p>
            </div>
            <button onClick={onDownloadTemplate} data-testid="download-template-btn"
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/15 text-white/60 font-black text-[10px] tracking-[0.15em] uppercase hover:border-white/30 hover:text-white transition-colors">
              <Download size={11} /> Plantilla
            </button>
          </div>

          {/* File input */}
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-white/50 mb-2">
              Archivo CSV o Excel (.xlsx)
            </label>
            <div
              onClick={() => inputRef.current?.click()}
              data-testid="import-file-dropzone"
              className="border border-dashed border-white/15 px-4 py-6 text-center cursor-pointer hover:border-[#FFCC00]/40 hover:bg-[#FFCC00]/5 transition-colors">
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx"
                className="hidden"
                data-testid="import-file-input"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); }}
              />
              {importFile ? (
                <div className="flex items-center justify-center gap-2 text-sm text-white">
                  <CheckCircle2 size={15} className="text-[#FFCC00]" />
                  <span className="font-bold">{importFile.name}</span>
                  <span className="text-white/30 text-xs">({(importFile.size / 1024).toFixed(1)} KB)</span>
                </div>
              ) : (
                <div>
                  <Upload size={20} className="mx-auto mb-2 text-white/25" />
                  <p className="text-sm text-white/40">Haz clic para seleccionar el archivo</p>
                  <p className="text-xs text-white/25 mt-0.5">.csv o .xlsx</p>
                </div>
              )}
            </div>
          </div>

          {/* Result */}
          {hasResult && (
            <div className="border border-white/10 bg-white/[0.02] px-4 py-4 space-y-2" data-testid="import-result">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/50 mb-3">Resultado de la importación</p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-green-500/8 border border-green-500/20 px-2 py-2">
                  <p className="text-2xl font-black text-green-300" data-testid="import-count-ok">{importResult.importados}</p>
                  <p className="text-[9px] tracking-wider uppercase text-green-400/70">Importados</p>
                </div>
                <div className="bg-yellow-500/8 border border-yellow-500/20 px-2 py-2">
                  <p className="text-2xl font-black text-yellow-300" data-testid="import-count-skip">{importResult.saltados_duplicados}</p>
                  <p className="text-[9px] tracking-wider uppercase text-yellow-400/70">Duplicados</p>
                </div>
                <div className="bg-red-500/8 border border-red-500/20 px-2 py-2">
                  <p className="text-2xl font-black text-red-300" data-testid="import-count-err">{importResult.errores?.length || 0}</p>
                  <p className="text-[9px] tracking-wider uppercase text-red-400/70">Errores</p>
                </div>
              </div>
              {importResult.errores?.length > 0 && (
                <div className="mt-2 space-y-1 max-h-28 overflow-y-auto">
                  {importResult.errores.map((e, i) => (
                    <div key={i} className="flex items-start gap-2 text-[11px] text-red-300/80">
                      <AlertCircle size={11} className="shrink-0 mt-0.5 text-red-400" />
                      <span>Fila {e.fila} · <span className="font-mono">{e.email}</span> — {e.motivo}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {importErr && <p className="text-red-400 text-sm" data-testid="import-error">{importErr}</p>}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-1">
            {hasResult ? (
              <button onClick={onClose} data-testid="import-close-btn"
                className="px-5 py-2.5 bg-[#FFCC00] text-black font-black text-[11px] tracking-[0.2em] uppercase hover:bg-white transition-colors">
                Cerrar
              </button>
            ) : (
              <>
                <button onClick={onClose} disabled={importing}
                  className="px-4 py-2.5 border border-white/15 text-white/60 font-black text-[11px] tracking-[0.2em] uppercase hover:border-white/30 hover:text-white transition-colors disabled:opacity-40">
                  Cancelar
                </button>
                <button onClick={onImport} disabled={!importFile || importing} data-testid="import-submit-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FFCC00] text-black font-black text-[11px] tracking-[0.2em] uppercase hover:bg-white transition-colors disabled:opacity-40">
                  {importing ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                  {importing ? "Importando..." : "Importar y enviar correos"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}